import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../app/store";
import { setAreaPreference, selectAreaPreferenceByPath } from "../app/slices/areaPreferencesSlice";

/**
 * Hook to read and write a single preference value for a given area.
 * Supports dot-path keys (e.g. "registrations.showOnlyConfirmed") for nested structures.
 * Changes are stored in Redux only. Use the save preferences button to persist to the API.
 */
export function useAreaPreference<T>(area: string, key: string, defaultValue: T): [T, (val: T) => void] {
    const dispatch = useDispatch();
    const value = useSelector((state: RootState) => {
        const resolved = selectAreaPreferenceByPath(state, area, key);
        return resolved ?? defaultValue;
    }) as T;

    const setValue = useCallback(
        (val: T) => {
            dispatch(setAreaPreference({ area, key, value: val }));
        },
        [dispatch, area, key]
    );

    return [value, setValue];
}
